import { message } from 'antd';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { determineTranscode } from '@/components/common/BatchUpload/common/utils';
import Dragger, { DraggerRule, ValidatorRule } from '@/components/common/Dragger';
import { usePartial } from '@/hooks/usePartial';
import { imageVerify as imageVerifyFn } from '@/services/common/imageVerify';
import { videoTranscode } from '@/services/common/videoTranscode';
import { fc_uploadStart } from '@/utils/eventlog/eventNewConfig';
import { GetFileMD5, getVideoInfo } from '@/utils/fileUtil';
import { t } from '@/utils/helper/intl';
import { FileUploader } from '@/utils/uploader';

import UploadError, { UploadErrorProps } from './uploadError';
import UploadForward, { UploadForwardProps } from './uploadForward';
import Uploading, { UploadUploadingProps } from './uploading';
import UploadReady, { UploadReadyProps } from './uploadReady';
import UploadSuccess, { UploadSuccessProps } from './uploadSuccess';
import UploadTranscoding from './uploadTranscoding';

type UploadStatus = '' | 'forward' | 'pending' | 'transcoding' | 'resolve' | 'reject';

type UploadData = {
  progress: number | string;
  status: UploadStatus;
  url: string;
  file: File | null;
};

export type DraggerUploadRef = {
  clear: () => void;
  getData: () => UploadData;
};

export type DraggerUploadProps = {
  height?: number;
  rules?: DraggerRule[];
  transcode?: boolean;
  imageVerify?: boolean;
  fileType: 'video' | 'audio' | 'image';
  ready?: UploadReadyProps & {
    onBefore?: (file: File) => Promise<boolean> | boolean;
  };
  forward?: UploadForwardProps;
  uploading?: Omit<UploadUploadingProps, 'percent'>;
  success?: Pick<UploadSuccessProps, 'slotNode' | 'onSuccess'>;
  error?: UploadErrorProps;
  eventTracking: {
    from: fc_uploadStart['from'];
  };
};
const DraggerUpload = forwardRef((props: DraggerUploadProps, ref) => {
  let uploader = useRef<FileUploader | null>(null);

  const readyRef = useRef<HTMLDivElement>(null);
  const forwardRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const transcodeCancelRef = useRef<() => void>(() => {});
  const needTransCodeRef = useRef<boolean>(false);
  const needImageVerify = useRef<boolean>(false);

  const [uploadOptions, setUploadOptions] = usePartial<UploadData>({
    progress: 0,
    status: '',
    url: '',
    file: null,
  });
  const {
    height = 190,
    rules = [],
    fileType,
    transcode = false,
    imageVerify = false,
    ready,
    forward = {},
    uploading,
    success,
    error,
    eventTracking,
  } = props;

  useEffect(() => {
    return () => {
      transcodeCancelRef.current();
    };
  }, []);

  const resetState = () => {
    transcodeCancelRef.current();
    needTransCodeRef.current = false;
    needImageVerify.current = false;
    setUploadOptions({
      progress: 0,
      status: '',
      url: '',
      file: null,
    });
  };

  // 暴露upload方法给父组件
  useImperativeHandle(ref, () => ({
    clear: resetState,
    getData: () => {
      return uploadOptions;
    },
  }));

  const handleCancelUpload = async () => {
    await uploader.current?.cancelUpload();
    resetState();
  };

  // 抽离的视频校验函数，复用传入的rules中的validator
  const validateVideoWithRules = async (
    file: File,
    videoUrl?: string,
  ): Promise<boolean> => {
    try {
      // 找到rules中的validator
      const validatorRule = rules.find(
        (rule) => rule.type === 'validator' && rule.validator,
      ) as ValidatorRule;
      if (!validatorRule) {
        return true; // 如果没有validator规则，默认通过
      }

      let targetFile = file;

      // 如果提供了videoUrl（转码后的视频），需要创建一个新的File对象来模拟转码后的文件
      if (videoUrl) {
        // 获取转码后视频的信息
        // const videoInfo = await getVideoInfo(videoUrl);

        // 创建一个临时的File对象，包含转码后的视频信息
        // 注意：这里主要是为了让validator能够校验转码后的视频分辨率等信息
        const response = await fetch(videoUrl);
        const blob = await response.blob();
        targetFile = new File([blob], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
      }

      // 创建FileList用于校验
      const fileList = {
        0: targetFile,
        length: 1,
        item: (index: number) => (index === 0 ? targetFile : null),
        [Symbol.iterator]: function* () {
          yield targetFile;
        },
      } as FileList;

      const isValid = await validatorRule.validator(fileList);
      return Boolean(isValid);
    } catch (error) {
      console.error('视频校验失败:', error);
      return false;
    }
  };
  const handleImageVerify = (file: File, url: string): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      const md5Image = (await GetFileMD5(file)) as string;

      const res = await imageVerifyFn({
        url,
        imageMd5: md5Image,
      });

      if (!res.success) {
        resolve(false);
      }

      if (res.data?.reviewState !== 1) {
        message.error(t('merror.swap.tip.pictureNotCompliantPleaseReselect'));
        resolve(false);
      }

      resolve(true);
    });
  };

  const onFileUpload = async (files: FileList) => {
    if (files[0]) {
      let resolution = '';
      if (fileType === 'video') {
        let { width, height } = await getVideoInfo(files[0]);
        resolution = width + '*' + height;
      }
      setUploadOptions({ file: files[0] });
      uploader.current = FileUploader.getInstance({
        onBefore: ready?.onBefore
          ? async ({ file }) => {
              setUploadOptions({
                status: 'forward',
              });
              if (ready.onBefore) {
                const result = await ready?.onBefore(file);

                if (!result) {
                  resetState();
                  return false;
                }
              }
              return true;
            }
          : undefined,
        onProgress: async ({ progress: progresNum, status, url }) => {
          const progress = Number(progresNum * 100).toFixed(2);
          if (!status) {
            setUploadOptions({
              progress,
              status: 'pending',
              url,
            });
          }
        },
        onSuccess: async (url: string, completeInfo) => {
          let resultVideoUrl = url;
          let resultCoverUrl = '';

          // 判断需不需要转码
          if (needTransCodeRef.current) {
            setUploadOptions({
              url: resultVideoUrl,
              status: 'transcoding',
            });
            const { fn, handler } = videoTranscode();
            transcodeCancelRef.current = handler;
            const { videoUrl, coverUrl } = await fn(url, true);

            // 转码后使用相同的validator规则校验视频
            if (videoUrl && fileType === 'video') {
              const isValidAfterTranscode = await validateVideoWithRules(
                files[0],
                videoUrl,
              );
              if (!isValidAfterTranscode) {
                // 转码后仍不符合validator规则，显示错误
                setUploadOptions({
                  status: 'reject',
                });
                return;
              }
            }
            resultVideoUrl = videoUrl || '';
            resultCoverUrl = coverUrl || '';
          } else if (needImageVerify.current) {
            // 判断需不需要图片审核
            const result = await handleImageVerify(files[0], url);
            if (!result) {
              setUploadOptions({
                status: '',
              });
              resetState();
              return false;
            }
          }
          success?.onSuccess?.(resultVideoUrl, files[0], { coverUrl: resultCoverUrl });

          setUploadOptions({
            url: resultVideoUrl,
            status: 'resolve',
          });
        },
        onCancel() {
          setUploadOptions({
            progress: 0,
            status: '',
          });
          transcodeCancelRef.current();
        },
        onError(error, fileId: string) {
          transcodeCancelRef.current();
          setUploadOptions({
            status: 'reject',
          });
        },
      });

      if (transcode) {
        // 开启转码校验
        needTransCodeRef.current = await determineTranscode(files[0]);
      }
      // 图片审核
      needImageVerify.current = imageVerify;
      try {
        await uploader.current?.upload({
          file: files[0],
          tokenData: {
            fileName: files[0].name,
            dirSceneType: needTransCodeRef.current ? 90 : 20,
          },
        });
      } catch (error) {
        console.error(error);
        return;
      }
    }
  };

  return (
    <div
      className="bg-[#F4F5FE] rounded-[8px] overflow-hidden border border-[#C5C5C5]"
      style={{ height: height + 'px' }}
    >
      {/* 默认展示 */}
      {uploadOptions.status === '' && (
        <Dragger rules={rules} onFileUpload={onFileUpload}>
          {<UploadReady {...ready} ref={readyRef} />}
        </Dragger>
      )}

      {/* 上传前的拦截展示 */}
      {uploadOptions.status === 'forward' && (
        <UploadForward {...forward} ref={forwardRef} />
      )}

      {/* 上传中 */}
      {uploadOptions.status === 'pending' && (
        <Uploading
          slotNode={uploading?.slotNode}
          percent={uploadOptions.progress}
          onCancel={handleCancelUpload}
          ref={loadingRef}
        />
      )}
      {/* 转码中 */}
      {uploadOptions.status === 'transcoding' && (
        <UploadTranscoding
          url={uploadOptions.url}
          onCancel={() => {
            needTransCodeRef.current = false;
            needImageVerify.current = false;
            transcodeCancelRef.current();
          }}
        />
      )}

      {/* 上传成功 */}
      {uploadOptions.status === 'resolve' && (
        <UploadSuccess
          {...success}
          file={uploadOptions.file!}
          url={uploadOptions.url}
          ref={successRef}
          onClear={resetState}
        />
      )}

      {/* 上传失败 */}
      {uploadOptions.status === 'reject' && (
        <UploadError {...error} ref={errorRef} onReUpload={resetState} />
      )}
    </div>
  );
});
export default DraggerUpload;
